import React, { useEffect, useMemo, useRef, useState } from "react";
import AnimatedFeedbackPopup from "../AnimatedFeedbackPopup";
import "./GymnasiumStage.css";

const GYM_CLICK_IDS = [
  "Skipping_Rope",
  "Pull_up_Bar",
  "KettleBell",
  "Dumbell",
  "Medicine_Ball",
  "Resistance_Band",
  "Foam_Roller",
];

function formatLabel(raw = "") {
  return String(raw).replace(/[_-]+/g, " ").trim();
}

export default function GymnasiumStage({ data, onDone, onSessionPointsChange }) {
  const objectRef = useRef(null);
  const cleanupRef = useRef(null);
  const doneRef = useRef(false);
  const clickedSetRef = useRef(new Set());
  const [clickedIds, setClickedIds] = useState([]);
  const [clickedLabels, setClickedLabels] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const popupTimerRef = useRef(null);

  const pointsPerClick = data.pointsPerClick ?? 5;
  const score = useMemo(() => clickedIds.length * pointsPerClick, [clickedIds.length, pointsPerClick]);

  useEffect(() => {
    onSessionPointsChange?.(score);
  }, [score, onSessionPointsChange]);

  useEffect(() => {
    return () => {
      if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const objectEl = objectRef.current;
    if (!objectEl || !data.svgAsset) return undefined;
    clickedSetRef.current = new Set();

    const wireSvg = () => {
      const svgDoc = objectEl.contentDocument;
      if (!svgDoc) return;

      const cleaners = [];

      GYM_CLICK_IDS.forEach((id) => {
        const node = svgDoc.getElementById(id);
        if (!node) return;
        node.style.cursor = "pointer";
        node.style.pointerEvents = "auto";

        const onNodeClick = () => {
          // SVG groups sometimes ignore style-only pointer-events; hard guard repeat clicks.
          if (clickedSetRef.current.has(id)) return;
          clickedSetRef.current.add(id);

          const label = formatLabel(node.getAttribute("pos") || id);
          setClickedIds((prev) => {
            if (prev.includes(id)) return prev;
            const next = [...prev, id];
            if (next.length === GYM_CLICK_IDS.length && !doneRef.current) {
              doneRef.current = true;
              onDone(next.length * pointsPerClick);
            }
            return next;
          });
          setClickedLabels((prev) => (prev.includes(label) ? prev : [...prev, label]));

          if (popupTimerRef.current) clearTimeout(popupTimerRef.current);
          setPopupOpen(true);
          popupTimerRef.current = setTimeout(() => setPopupOpen(false), 850);

          node.removeEventListener("click", onNodeClick);
          node.setAttribute("pointer-events", "none");
          node.style.pointerEvents = "none";
          node.style.cursor = "default";
          node.style.opacity = "1";
          node.querySelectorAll("*").forEach((child) => {
            child.setAttribute("pointer-events", "none");
            child.style.pointerEvents = "none";
            child.style.cursor = "default";
          });
        };

        node.addEventListener("click", onNodeClick);
        cleaners.push(() => node.removeEventListener("click", onNodeClick));
      });

      cleanupRef.current = () => cleaners.forEach((fn) => fn());
    };

    objectEl.addEventListener("load", wireSvg);
    if (objectEl.contentDocument && objectEl.contentDocument.readyState === "complete") {
      wireSvg();
    }

    return () => {
      objectEl.removeEventListener("load", wireSvg);
      if (cleanupRef.current) cleanupRef.current();
      cleanupRef.current = null;
      doneRef.current = false;
      clickedSetRef.current = new Set();
    };
  }, [data.svgAsset, onDone, pointsPerClick]);

  return (
    <div className="gymnasium-stage">
      <div className="gymnasium-stage__svg-wrap">
        <object ref={objectRef} data={data.svgAsset} type="image/svg+xml" className="gymnasium-stage__object">
          Gym stage
        </object>
      </div>
      <div className="gymnasium-stage__bottom">
        {clickedLabels.length ? (
          <div className="gymnasium-stage__labels">
            {clickedLabels.map((label) => (
              <div key={label} className="gymnasium-stage__label">
                {label}
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <AnimatedFeedbackPopup open={popupOpen} type="success" title="Awesome!" />
    </div>
  );
}
